import {
  DataService,
  Utilities,
  WorkflowContextService
} from 'src/app/services';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { faTimes, faGlobe } from '@fortawesome/free-solid-svg-icons';
import {
  Component,
  OnInit,
  Input,
  Output,
  ViewChild,
  QueryList,
  ViewChildren,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ViewEncapsulation,
  ContentChild,
  TemplateRef,
  AfterViewChecked,
  AfterViewInit,
  ElementRef
} from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import {
  FilterBuilderParam,
  FilterBuilderOutput,
  SearchFilterBuilderOutput,
  FilterBuilderFilter,
  FilterBuilderFilterFlat,
  FilterBuilderParamOpt,
  FilterValueTransformFunction
} from 'src/app/models/filter-builder';
import {
  ApplyFilter,
  GetFilter,
  FilterSet,
  ColumnSet,
  SavedFilter,
  SavedColumn
} from 'src/app/models/saved-filter';
import { ModalConfirmComponent } from '../modal-confirm/modal-confirm.component';
import { ExportEntityOption } from '../../filter-list/models/filterClasses';
import { ShapefileValidationCriteria } from 'src/app/models/scheduled-export';
import { ActivatedRoute } from '@angular/router';
import { CommonDropdownData } from '../../common/common-dropdown/common-dropdown.component.model';
import { FilterService } from 'src/app/services/filters.service';
import { CommonConfirmationModalData } from '../../common/common-confirmation-modal/common-confirmation-modal.component.model';

const defaultTransforms: {
  [inputType: string]: FilterValueTransformFunction;
} = {
  date: (value: string) => {
    if (!value) {
      return '(unset)';
    }
    return moment(value).format('MM/DD/YYYY');
  },
  ['datetime-local']: (value: string) => {
    if (!value) {
      return '(unset)';
    }
    return moment(value).format('MM/DD/YYYY h:mm a');
  }
};

type ColumnsDropdownMode = | 'export-only' | 'show-only' | 'show-export'; // added by Vipul Rathod on 17/07/2026

@Component({
  selector: 'wm-filter-builder',
  templateUrl: './filter-builder.component.html',
  styleUrls: ['./filter-builder.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class FilterBuilderComponent
  implements OnInit, OnChanges, AfterViewInit {
  @ViewChildren('popovers') filterPopovers: QueryList<NgbPopover>;
  @ViewChild('fp', { static: false }) filterPopover: NgbPopover;
  @ViewChild('columnOptionsModal', { static: false })
  columnOptionsModal: ModalConfirmComponent;
  @Input() savedFilters = false;
  @Input() params: FilterBuilderParam[] = [];
  @Input() filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'not contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    },
    {
      name: 'is not'
    },
    {
      name: 'exists'
    },
    {
      name: 'not exists'
    },
    {
      name: '<'
    },
    {
      name: '>'
    },
    {
      name: '<='
    },
    {
      name: '>='
    }
  ];
  @Input() id: string;
  @Input() waitForParent = false;
  @Input() exportingEnabled = false;
  @Input() parentId: string;
  @Input() shapefileValidationCriteria: ShapefileValidationCriteria;

  @Output() filtersChange: EventEmitter<
    FilterBuilderOutput
  > = new EventEmitter();

  @Output() filtersChange2: EventEmitter<
    SearchFilterBuilderOutput
  > = new EventEmitter();
  @Input() showFilterBuilder = true;
  @Input() showSimpleSearch = true;
  @Input() showColumnsSettings = true;

  @Output() columnsChange: EventEmitter<ColumnSet> = new EventEmitter<
    ColumnSet
  >();
  @Output() exportColumnsChange: EventEmitter<string[]> = new EventEmitter<
    string[]
  >();

  @Input() columnOptions: any[];
  @Input() onlyExportOptions = false;
  // Added by Amisha on 2026-06-15.
  // Reason: keep the Janus scheduled export column-settings layout opt-in so non-Janus
  // and other shared filter-builder consumers continue using the existing menu markup.
  @Input() janusScheduledExport = false;
  @Input() showExportOption = false; // Code added by Sanchit on 07/01/2026
  @Input() contractorTypeColumnsPopup = false; // Code added by Sanchit on 07/01/2026

  private columnsDropdownMode: ColumnsDropdownMode; // added by Vipul Rathod on 17/07/2026
  columnsDropdownData: CommonDropdownData;  // added by Vipul Rathod on 17/07/2026
  columnVisibilityDropdownData: CommonDropdownData; // added by Vipul Rathod on 17/07/2026

  private filtersChangedSubject: Subject<FilterBuilderOutput> = new Subject<
    FilterBuilderOutput
  >();
  private filtersChangedSubject2: Subject<
    SearchFilterBuilderOutput
  > = new Subject<SearchFilterBuilderOutput>();
  private simpleSearchSubject: Subject<string> = new Subject<string>();

  defaultDateRangeOpts = [
    {
      name: 'Today',
      value: 'this_1_days'
    },
    {
      name: 'Yesterday',
      value: 'previous_1_day'
    },
    {
      name: 'Current Week',
      value: 'this_1_weeks'
    },
    {
      name: 'Previous Week',
      value: 'previous_1_weeks'
    },
    {
      name: 'Current Month',
      value: 'this_1_months'
    },
    {
      name: 'Last Month',
      value: 'previous_1_months'
    },
    {
      name: 'Current Calendar Quarter',
      value: 'this_1_calendarquarter'
    },
    {
      name: 'Previous Calendar Quarter',
      value: 'previous_1_calendarquarter'
    },
    {
      name: 'Current Year',
      value: 'this_1_years'
    },
    {
      name: 'Last Year',
      value: 'previous_1_years'
    },
    {
      name: 'Custom',
      value: 'custom'
    }
  ];
  filters: FilterBuilderFilter[] = [];
  @Input() defaultFilters: FilterBuilderFilter[];
  @Input() simpleSearchTitle = 'Search for applications!';
  // Added by Amisha on 2026-05-21.
  // Reason: applicant Janus reuses this shared filter builder on multiple tabs,
  // so the section heading must be configurable instead of always showing
  // "My Applications".
  @Input() layoutTitle = 'My Applications';

  faTimes = faTimes;
  faGlobe = faGlobe;
  allTriggered = false;
  hasAutoTriggered = false;

  searchModel: string;
  simpleSearchModel: string;
  simpleFilters: FilterBuilderFilter[] = [];
  trialColumnOptions: any[] = [];
  // added by priya 04-02-2026
  @Input({ required: false }) public useMuiLayout: boolean = false;
  @Input({ required: false }) public isDataSetFilterButton: boolean = false;
  // end by priya 04-02-2026
  // added by Vipul on 23/02/2026
  @Input() staffFilterBuilder?: boolean;

  filterDropdownData: CommonDropdownData;
  editDropdownData: CommonDropdownData;
  savedFilterList: SavedFilter[] = [];
  savedFilterDropdownData: CommonDropdownData;
  selectedSavedFilterId: string = null;
  private filterToOpenId: string | null = null;
  private isAddingFilter = false;

  // Confirmation Modal details
  saveModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    okBtn: true,
    yesNoBtn: false,
    closeBtn: false,
    status: 'default'
  };

  saveCloneMode = false;
  saveFilterName = '';

  openSaveModal(clone: boolean = false) {
    this.saveCloneMode = clone;

    if (!clone && this.selectedSavedFilterId) {
      const existing = this.savedFilterList.find(
        f => f.id === this.selectedSavedFilterId
      );
      this.saveFilterName = existing?.name || '';
    } else {
      this.saveFilterName = '';
    }

    this.saveModalData = {
      ...this.saveModalData,
      open: true,
      modalDescp: clone ? 'Save Filter As' : 'Save Filter',
      replaceOkWith: 'Save'
    };
  }

  async handleSaveModalOk() {
    if (!this.saveFilterName?.trim()) return;

    await this.saveCurrentFilter(
      this.saveFilterName.trim(),
      this.saveCloneMode
    );
  }

  private buildFilterDropdown() {
    this.filterDropdownData = {
      placeholder: 'FILTER BY',
      prefixIcon: 'filter_alt',
      actionMode: true,
      selectedValue: null,
      options: (this.params || []).map(item => ({
        label: item.name,
        value: item.id
      }))
    };
  }

  onFilterDropdownChange(paramId: string) {
    const param = this.params.find(p => p.id === paramId);

    if (!param) {
      console.warn('Param not found for id:', paramId);
      return;
    }

    this.addFilter(param);
  }

  private buildEditDropdown() {
    this.editDropdownData = {
      placeholder: 'EDIT',
      prefixIcon: 'filter_alt',
      actionMode: true,
      options: [
        { label: 'Save', value: 'save' },
        { label: 'Save as', value: 'saveAs' },
        { label: 'Delete', value: 'delete' }
      ]
    };
  }

  getFilterDropdownData(filter: FilterBuilderFilter): CommonDropdownData {
    return {
      placeholder: 'Select an option',
      selectedValue: filter.value ?? null,
      options: this.getFilterOptions(filter.param).map(opt => ({
        label: opt.name,
        value: opt.value
      }))
    };
  }

  onFilterValueChange(filter: FilterBuilderFilter, value: any) {
    filter.value = value;

    // keep legacy compatibility
    this.filtersChanged();
  }

  // Load saved filters from backend API
  private async loadSavedFilters() {
    if (!this.id) return;

    this.savedFilterList = await this.filterSvc.list(
      this.context.client?.id,
      this.id
    );

    this.buildSavedFilterDropdown();
  }

  // Saved Filter dropdown data
  private buildSavedFilterDropdown() {
    this.savedFilterDropdownData = {
      placeholder: 'SAVED FILTERS',
      prefixIcon: 'filter_alt',
      selectedValue: this.selectedSavedFilterId,
      options: [
        ...this.savedFilterList.map(filter => ({
          label: filter.name.toUpperCase(),
          value: String(filter.id)
        })),
        {
          label: '(NEW FILTER)',
          value: '__new__'
        }
      ]
    };
  }

  // Saved Filter dropdown change method
  async onSavedFilterChange(filterId: string) {
    if (filterId === '__new__') {
      this.selectedSavedFilterId = null;

      this.savedFilterDropdownData = {
        ...this.savedFilterDropdownData,
        selectedValue: null
      };
      // Start Code added by Sanchit on 07/01/2026
      if (this.staffFilterBuilder) {
        // Reset filters
        this.filters = [];
        this.simpleFilters = [];
        this.searchModel = '';
        this.simpleSearchModel = '';
        this.hasAutoTriggered = false;

        // Show all columns
        if (this.columnOptions) {
          this.columnOptions.forEach(column => {
            column.checked = true;
            column.includeInExport = true;
          });
        }

        // Emit empty filters
        this.filtersChanged();

        // Emit updated columns
        if (this.columnOptions) {
          if (this.onlyExportOptions) {
            this.updateAndEmitExportColumns();
          } else {
            this.updateAndEmitColumnSet();
          }
        }

        return;
      }
      // Ed Code added by Sanchit on 07/01/2026

      await this.applyFilter({
        filters: [],
        columns: [],
        exportColumns: []
      });

      return;
    }

    this.selectedSavedFilterId = filterId;

    this.savedFilterDropdownData = {
      ...this.savedFilterDropdownData,
      selectedValue: filterId
    };

    const selected = this.savedFilterList.find(f => f.id === filterId);

    if (!selected) {
      await this.applyFilter({
        filters: [],
        columns: [],
        exportColumns: []
      });
      return;
    }

    await this.applyFilter({
      filters: selected.filters,
      columns: selected.columns,
      exportColumns: selected.exportColumns
    });
  }

  async saveCurrentFilter(name: string, clone?: boolean) {
    const currentFilter = await this.getCurrentFilter();

    const filter: SavedFilter = {
      id:
        clone || !this.selectedSavedFilterId
          ? Utilities.generateId()
          : this.selectedSavedFilterId,
      name,
      type: this.id,
      clientId: this.context.client?.id,
      filters: currentFilter.filters,
      columns: currentFilter.columns,
      exportColumns: currentFilter.exportColumns
    };

    await this.filterSvc.upsert(filter.id, filter);

    await this.loadSavedFilters();
    this.selectedSavedFilterId = filter.id;
    this.buildSavedFilterDropdown();
  }

  async deleteSavedFilter(id: string) {
    await this.filterSvc.destroy(id);

    this.selectedSavedFilterId = null;
    await this.loadSavedFilters();

    await this.applyFilter({
      filters: [],
      columns: [],
      exportColumns: []
    });
  }

  onEditAction(action: string) {
    switch (action) {
      case 'save':
        this.openSaveModal(false);
        break;

      case 'saveAs':
        this.openSaveModal(true);
        break;

      case 'delete':
        if (this.selectedSavedFilterId) {
          this.deleteSavedFilter(this.selectedSavedFilterId);
        }
        break;
    }
  }

  ngAfterViewInit() {
    this.filterPopovers.changes.subscribe(() => {
      if (!this.filterToOpenId) return;

      const popover = this.filterPopovers
        .toArray()
        .find(
          (p: any) => p._nativeElement?.id === `filter-${this.filterToOpenId}`
        );

      if (popover) {
        if (this.staffFilterBuilder) {
          setTimeout(() => {
            this.openFilter(popover);
          });
        } else {
          setTimeout(() => popover.open());
        }
        this.filterToOpenId = null;
      }
    });
  }

  // ngAfterViewChecked() {
  //   if (!this.filterToOpenId) return;

  //   const popover = this.filterPopovers
  //     ?.toArray()
  //     ?.find((p: any) =>
  //       p._nativeElement?.id === `filter-${this.filterToOpenId}`
  //     );

  //   if (popover) {
  //     this.openFilter(popover);
  //     this.filterToOpenId = null; // reset
  //   }
  // }
  // end by Vipul on 23/02/2026

  get filteredParams() {
    if (this.searchModel) {
      return this.params.filter(param =>
        param.name.toLowerCase().includes(this.searchModel.toLowerCase())
      );
    } else {
      return this.params;
    }
  }

  get cacheId(): string {
    return `filter-builder-${this.id}`;
  }

  get selectedExportColumnsNumber() {
    return (
      this.columnOptions &&
      this.columnOptions.filter(opt => opt.includeInExport).length
    );
  }

  constructor(
    public context: WorkflowContextService,
    private route: ActivatedRoute,
    private _service: DataService,
    private filterSvc: FilterService // added by Vipul on 24-02-2026
  ) {
    this.filtersChangedSubject
      .pipe(
        debounceTime(300),
        // stringify the JSON so that we can compare the objects
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(filterOutputs => {
        this.filtersChange.emit(filterOutputs);
      });

    this.filtersChangedSubject2
      .pipe(
        debounceTime(300),
        // stringify the JSON so that we can compare the objects
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(filterOutputs2 => {
        this.filtersChange2.emit(filterOutputs2);
      });

    this.simpleSearchSubject.pipe(debounceTime(500)).subscribe(value => {
      this.changeSimpleSearch();
    });
  }
  ngOnInit() {
    // added by Vipul on 24/02/2026
    if (this.savedFilters) {
      this.loadSavedFilters();
    }
    // end by Vipul on 24/02/2026
    // added by priya 04-02-2026
    if (this.useMuiLayout) {
      const style = document.createElement('style');
      style.id = 'disable-popover-style';
      style.innerHTML = `
        ngb-popover-window {
          border: none !important;
          box-shadow: none !important;
              width: auto;
    height: auto;
    box-shadow: 0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12) !important;
    border-radius: 16px !important;
        }
      `;
      document.head.appendChild(style);
    }
    // end by priya 04-02-2026

    // don't show the filter builder if there aren't any parameters defined.
    if ((this.params || []).length == 0) {
      this.showFilterBuilder = false;
    }
    // restore filters from localStorage
    this.filters = this.getCachedFilters() || this.filters;
    if (this.filters.length === 0 && this.defaultFilters) {
      this.filters = this.defaultFilters;
    }
    this.filtersChanged();

    //Added by Vipul on 23/02/2026
    if (this.staffFilterBuilder) {
      this.buildFilterDropdown();
      this.buildEditDropdown();
      // Added by Vipul Rathod on 17/07/2026
      if (this.onlyExportOptions) {
        this.refreshColumnsDropdown('export-only');
      } else if (this.exportingEnabled) {
        this.refreshColumnsDropdown('show-export');
      } else {
        this.refreshColumnsDropdown('show-only');
      }
      // End by Vipul Rathod on 17/07/2026
    }
    //End by Vipul on 23/02/2026
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.defaultFilters) {
      this.filters = this.defaultFilters;
    }
  }

  isGeoJSON(column: any) {
    if (
      this.shapefileValidationCriteria &&
      this.shapefileValidationCriteria.geoJSONEntities != null
    ) {
      const isgeoJSONEntity = this.shapefileValidationCriteria.geoJSONEntities[
        column.name
      ];
      if (isgeoJSONEntity) {
        return true;
      }
    }
    return false;
  }
  /**
   * Get filters that are cached in localStorage
   */
  private getCachedFilters(): FilterBuilderFilter[] {
    if (!this.id) {
      return [];
    }

    try {
      const stringData = localStorage.getItem(this.cacheId);

      if (!stringData) {
        return [];
      }

      const jsonData = JSON.parse(stringData);

      const data: FilterBuilderFilter[] = jsonData
        .map((filter: FilterBuilderFilterFlat) => {
          try {
            const param = this.params.find(p => p.id === filter.paramId);

            if (!param) {
              return null;
            }

            return FilterBuilderFilter.unflatten({ ...filter, param });
          } catch (err) {
            // if there was an error, the param probably doesn't exist anymore so we should just swallow the error and then remove this filter
            return null;
          }
        })
        .filter(filter => !!filter); // remove dead filters
      return data;
    } catch (error) {
      return [];
    }
  }

  /**
   * Set filters cache in localStorage
   */
  private setCachedFilters(filters: FilterBuilderFilter[]) {
    if (!this.id) {
      return;
    }

    const flattenedFilters = filters.map(filter => filter.flatten());

    const stringData = JSON.stringify(flattenedFilters);
    localStorage.setItem(this.cacheId, stringData);
  }
  isFilterAdded(param: FilterBuilderParam): boolean {
    return this.filters.some(f => f.paramId === param.id);
  }
  @ViewChild('filterBtn') filterBtn!: ElementRef;

  dropdownMaxHeight = 680;
  showFilters(pop: NgbPopover, btn?: HTMLElement) {
    if (!this.useMuiLayout) {
      this.filterPopovers.forEach(pop => pop.close());
      this.filterPopover.toggle();
    } else {
            //added by priya 25-02-2026

      if (!pop) return;

      const target = btn ?? this.filterBtn.nativeElement;
      const rect = target.getBoundingClientRect();

      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement = spaceBelow >= spaceAbove ? 'bottom' : 'top';

      this.currentPlacement = placement;
      pop.placement = placement;

      const reservedHeight = 90;

      this.dropdownMaxHeight = Math.max(
        200,
        Math.min(
          680,
          (placement === 'bottom' ? spaceBelow : spaceAbove) - reservedHeight
        )
      );

      this.filterPopovers.forEach(p => {
        if (p !== pop) {
          p.close();
        }
      });

      if (!pop.isOpen()) {
        pop.open();
      }

      if (this.context.janusLayout === 'applicant') {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            btn?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          });
        });
      }
      //end by priya 25-02-2026
    }
  }
  addFilter(param: FilterBuilderParam, btn?: HTMLElement) {
    if (this.useMuiLayout) {
      const alreadyExists = this.filters.some(f => f.paramId === param.id);

      if (alreadyExists) {
        return;
      }
    }
    const filter = new FilterBuilderFilter({
      paramId: param.id,
      param
    });
    this.filters.push(filter);
    // added by Vipul on 24/02/2026
    if (this.filterPopover) {
      this.filterPopover.close();
    }

    if (!this.staffFilterBuilder) {
      setTimeout(() => {
        // find the popover and open it
        const popover = this.filterPopovers?.find(
          (p: any) => p?._nativeElement?.id === `filter-${filter.id}`
        );
        if (!popover) {
          console.warn('Popover not found for filter:', filter.id);
          return;
        }
        this.openFilter(popover);
        //added by priya 25-02-2026

        if (this.context?.janusLayout === 'applicant' && btn) {
          setTimeout(() => {
            btn?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }, 50);
        }

        //end by priya 25-02-2026
      }, 10);
    } else if (this.staffFilterBuilder) {
      this.filterToOpenId = filter.id;
    }

    this.filtersChanged();
  }

  removeFilter(filter: FilterBuilderFilter) {
    this.filters = this.filters.filter(f => f.id !== filter.id);
    this.filtersChanged();
  }

  openFilter(popover: NgbPopover, chipEl?: HTMLElement) {
    const isOpen = popover.isOpen(); //added by priya 25-02-2026
    this.openOptionMenu = false;
    this.openRangeMenu = false;

    if (!popover) return;
    this.filterPopovers.forEach(pop => {
      if (pop !== popover) {
        pop.close();
      }
    });
    if (this.filterPopover) {
      this.filterPopover.close();
    }
    if (this.isDataSetFilterButton) {
      if (!isOpen) {
        popover.open();
      }
    } else {

      popover.toggle();
    }

    // Added by Vipul Rathod on 30-07-2026
    if (this.staffFilterBuilder) {
      if (!isOpen) {
        popover.open();

        requestAnimationFrame(() => {
          const host = (popover as any)._nativeElement as HTMLElement;
          if (host) {
            this.lockParentScroll(host);
          }
        });
      } else {
        popover.close();
        this.unlockParentScroll();
      }
    }
    // end by Vipul Rathod on 30-07-2026

    //added by priya 25-02-2026
    // ✅ scroll to center when opening
    if (!isOpen && chipEl) {
      requestAnimationFrame(() => {
        chipEl.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      });
    }
  }

  private transformFilterValue(filter: FilterBuilderFilter): string {
    // if there are pre-defined options for this param, map them to a KV object so they are easy to use later
    const optionsObject = {};
    if (filter.param.options) {
      for (const opt of filter.param.options) {
        if (typeof opt === 'object') {
          // if it is an object, there is a name and a value
          const optObject = opt as FilterBuilderParamOpt;
          optionsObject[optObject.value] = optObject.name;
        } else {
          // if it is just a string, there is only a value
          optionsObject[opt] = opt;
        }
      }
    }

    if (filter.param.valueTransform) {
      // first, if there is a value transform, use it
      return filter.param.valueTransform(filter.value);
    } else if (filter.param.options && filter.queryType !== 'contains') {
      // then, if there are pre-defined options, use the option name
      return optionsObject[filter.value];
    } else if (defaultTransforms[filter.param.inputType]) {
      // next, if there is a default transform for the input type, use it
      const transformFn = defaultTransforms[filter.param.inputType];
      return transformFn(filter.value);
    } else {
      // otherwise, fallback to the value
      return filter.value;
    }
  }

  filterText(filter: FilterBuilderFilter) {
    if (filter.queryType === 'exists') {
      return 'exists';
    }

    if (filter.queryType === 'not exists') {
      return 'not exists';
    }

    if (filter.queryType === 'range') {
      if (filter.value && filter.value !== 'custom') {
        const range = this.defaultDateRangeOpts.find(
          opt => opt.value === filter.value
        );

        if (!range) {
          return '';
        }

        return range.name;
      }

      if (filter.customValue) {
        const transformFn = defaultTransforms[filter.param.inputType];

        let start = filter.customValue.start;
        let end = filter.customValue.end;

        if (transformFn) {
          start = transformFn(start);
          end = transformFn(end);
        } else {
          start = start || '(unset)';
          end = end || '(unset)';
        }
        return `from ${start} to ${end}`;
      }

      return '';
    } else if (filter.queryType === 'in' && Array.isArray(filter.customValue)) {
      const list = filter.customValue.map(value => {
        const tempFilter = Object.assign({}, filter);
        tempFilter.value = value;

        return this.transformFilterValue(tempFilter);
      });

      return `${filter.queryType} ${list.join(', ')}`;
    } else if (filter.queryType && filter.value) {
      const plainTextValue = this.transformFilterValue(filter);

      return `${filter.queryType} ${plainTextValue}`;
    }
    return '';
  }

  getFilterTypes(param: FilterBuilderParam) {
    let filterTypes = [...this.filterTypes];

    // only allow the range filter if it's a date or text
    if (
      param.inputType !== 'date' &&
      param.inputType !== 'datetime-local' &&
      param.inputType !== 'text' &&
      param.inputType !== 'number'
    ) {
      filterTypes = filterTypes.filter(t => t.name !== 'range');
    }

    // only allow the 'in' filter type if there's a collection of values to choose from
    if (param.options == null) {
      filterTypes = filterTypes.filter(t => t.name !== 'in');
    }

    if (param.types) {
      filterTypes = filterTypes.filter(t => param.types.includes(t.name));
    }

    return filterTypes;
  }

  // Cache normalized options per param so *ngFor keeps stable references across
  // change-detection cycles. Without this, a new array/objects were returned on
  // every CD tick, causing Angular to destroy and recreate every .menu-item DOM
  // node in the "in"-filter dropdown. That in turn prevented the browser from
  // dispatching a `click` (mousedown/mouseup landed on different nodes), so the
  // items were not selectable when the staff/new-layout filter builder was used.
  private _normalizedOptionsCache = new WeakMap<
    FilterBuilderParam,
    FilterBuilderParamOpt[]
  >();

  getFilterOptions(param: FilterBuilderParam): FilterBuilderParamOpt[] {
    let normalized = this._normalizedOptionsCache.get(param);

    if (!normalized) {
      normalized = param.options.map(opt => {
        if (typeof opt === 'string') {
          return { name: opt, value: opt };
        } else {
          return opt as FilterBuilderParamOpt;
        }
      });

      this._normalizedOptionsCache.set(param, normalized);
    }

    return normalized;
  }

  getFilterRangeOptions(param: FilterBuilderParam): FilterBuilderParamOpt[] {
    return param.rangeOptions || this.defaultDateRangeOpts;
  }
  openRangeMenu = false;
  openOptionMenu = false;
  getSelectedRangeName(filter: any): string | null {
    const opt = this.getFilterRangeOptions(filter.param).find(
      o => o.value === filter.value
    );
    return opt?.name || null;
  }
  getSelectedOptionName(filter: any): string | null {
    const opt = this.getFilterOptions(filter.param).find(
      o => o.value === filter.value
    );
    return opt?.name || null;
  }
  isSelected(filter, value) {
    const selected =
      Array.isArray(filter.customValue) &&
      filter.customValue.includes(value);
    return selected;
  }
    getSelectedOptionInQuery(filter) {

  const selectedValues = String(filter.value || '')
  .split(',')
  .filter(Boolean);

return this.getFilterOptions(filter.param)
  .filter(opt => selectedValues.includes(opt.value))
  .map(opt => opt.name)
  .join(', ');

  }
  onDateRangeSelect(filter: any, opt: any): void {
    if (filter.value === opt.value) return;

    filter.value = opt.value;
    this.filtersChanged();

    this.openRangeMenu = false;
  }
  bypassBinding(f, e) {
    f.value = e.srcElement.value;
    this.filtersChanged();
  }

  filtersChanged() {
    if (this.isAddingFilter) {
      return;
    }
    let submissionFilter: FilterBuilderFilter[];
    let submissionFilter2: FilterBuilderFilter[];
    setTimeout(() => {
      // on 20220415 we decided to simple search on available results
      // when there were filters set.
      // previously we removed simple filters from the filters.

      // remove any filters from the simple filters...
      const results = this.simpleFilters.filter(
        a =>
          !this.filters.some(b => {
            return a.param.id === b.param.id;
          })
      );

      submissionFilter = this.filters.concat(results);
      submissionFilter2 = this.filters.concat(this.simpleFilters);

      const filterOutputs: FilterBuilderOutput = {};
      const filterOutputs2: SearchFilterBuilderOutput = {};

      // do the submission filter with only one option.
      for (const filter of submissionFilter) {
        let value = filter.value;
        if (
          filter.queryType === 'range' &&
          (filter.value === 'custom' ||
            filter.param.inputType === 'text' ||
            filter.param.inputType == 'number') &&
          filter.customValue &&
          filter.customValue.start &&
          filter.customValue.end
        ) {
          // if it is a date range, parse the value into a single string
          value = JSON.stringify(filter.customValue);
        } else if (
          filter.queryType === 'range' &&
          (filter.value === 'custom' ||
            filter.param.inputType === 'text' ||
            filter.param.inputType == 'number')
        ) {
          // if it is a custom date range that hasn't been set yet, make value null
          value = null;
        } else if (
          filter.queryType === 'in' &&
          Array.isArray(filter.customValue)
        ) {
          value = filter.customValue.join(',');
        }

        // only use the filter if it actually is complete
        if (
          value ||
          filter.queryType == 'exists' ||
          filter.queryType == 'not exists'
        ) {
          filterOutputs[filter.param.id] = {
            id: filter.param.id,
            type: filter.queryType,
            searchOptionMethod: filter.searchOptionMethod,
            value,
            inputType: filter.param.inputType
          };
        }
      }

      // do the submissionFilter with multiple options
      // currently the only thing listening to this is datatable component so the contractor-registration-list can react to it,
      // but to limit the scope of testing it is essentially doing the same thing as above, but this is returning
      // multiple search options in an array instead of just one.

      for (const filter of submissionFilter2) {
        let value = filter.value;
        if (
          filter.queryType === 'range' &&
          (filter.value === 'custom' ||
            filter.param.inputType === 'text' ||
            filter.param.inputType == 'number') &&
          filter.customValue &&
          filter.customValue.start &&
          filter.customValue.end
        ) {
          // if it is a date range, parse the value into a single string
          value = JSON.stringify(filter.customValue);
        } else if (
          filter.queryType === 'range' &&
          (filter.value === 'custom' ||
            filter.param.inputType === 'text' ||
            filter.param.inputType == 'number')
        ) {
          // if it is a custom date range that hasn't been set yet, make value null
          value = null;
        } else if (
          filter.queryType === 'in' &&
          Array.isArray(filter.customValue)
        ) {
          value = filter.customValue.join(',');
          console.log('filter customValue', value); // debugging on server purpose only
        }

        // only use the filter if it actually is complete
        if (
          value ||
          filter.queryType == 'exists' ||
          filter.queryType == 'not exists'
        ) {
          filterOutputs2[filter.param.id] =
            filter.param.id in filterOutputs2
              ? filterOutputs2[filter.param.id]
              : [];
          filterOutputs2[filter.param.id].push({
            id: filter.param.id,
            type: filter.queryType,
            searchOptionMethod: filter.searchOptionMethod,
            value,
            inputType: filter.param.inputType
          });
        }
      }
      this.setCachedFilters(this.filters);

      this.filtersChangedSubject2.next(filterOutputs2);
      this.filtersChangedSubject.next(filterOutputs);
    }, 10);
  }

  simpleSearchKeyUp(e) {
    this.simpleSearchSubject.next(e);
  }

  changeSimpleSearch() {
    this.simpleFilters.length = 0;

    if (this.simpleSearchModel) {
      // go through all the fields and add this filter....
      let tempString = '';
      this.params.forEach(value => {
        const p: FilterBuilderParam = {
          name: value.name,
          id: value.id
        };
        tempString += '"' + value.id + '",';

        const filter = new FilterBuilderFilter({
          value: this.simpleSearchModel.trim(),
          queryType: 'contains',
          param: p,
          paramId: p.id,
          searchOptionMethod: 'simpleSearch'
        });

        this.simpleFilters.push(filter);
      });
    }
    this.filtersChanged();
  }
  changeFilterType(filter: FilterBuilderFilter) {
    filter.value = null;
    filter.customValue = {};

    this.filtersChanged();
  }

  triggerAll(checked: boolean) {
    if (this.columnOptions && this.onlyExportOptions) {
      for (const column of this.columnOptions) {
        column.includeInExport = checked;
      }
    }

    this.filtersChanged();
  }

  translateColumns(): ColumnSet {
    if (!this.columnOptions) {
      return null;
    }

    // for the time being, this is the flag that tells us if the auto-trigger should happen
    // workflow-export.component is currently the only thing that should do that
    if (this.onlyExportOptions) {
      let anyChecked = false;
      for (const opt of this.columnOptions) {
        if (opt.includeInExport) {
          anyChecked = true;
        }
      }

      if (!anyChecked && !this.hasAutoTriggered) {
        this.triggerAll(true);
        this.allTriggered = true;
        this.hasAutoTriggered = true;
      }
    }

    const columns = this.columnOptions
      .filter(opt => opt.checked === true)
      .map(opt => ({
        header: opt.label,
        name: opt.name
      }));
    const exportColumns = this.columnOptions
      .filter(opt => opt.includeInExport === true)
      .map(opt => ({
        header: opt.label,
        name: opt.name
      }));

    return { columns, exportColumns };
  }

  getCurrentFilter: GetFilter = async () => {
    let translatedColumns: ColumnSet = null;

    if (this.columnOptions) {
      translatedColumns = this.translateColumns();
    }

    return {
      filters: this.filters.map(filter => filter.flatten()),
      columns: translatedColumns ? translatedColumns.columns : [],
      exportColumns: translatedColumns ? translatedColumns.exportColumns : []
    };
  };

  applyFilter: ApplyFilter = async (savedFilter: FilterSet) => {
    // if no saved filter is selected, don't change filters
    // Start Code added by Sanchit on 07/01/2026
    if (this.staffFilterBuilder) {
      this.hasAutoTriggered = false;

      // Always clear previous chips
      this.filters = [];

      const data: FilterBuilderFilter[] = (savedFilter.filters || [])
        .map((filter: FilterBuilderFilterFlat) => {
          try {
            const param = this.params.find(p => p.id === filter.paramId);

            if (!param) {
              return null;
            }

            return FilterBuilderFilter.unflatten({ ...filter, param });
          } catch {
            return null;
          }
        })
        .filter(filter => !!filter);

      this.filters = data;
    } else if (savedFilter.filters.length > 0) {
      this.hasAutoTriggered = false;

      const data: FilterBuilderFilter[] = savedFilter.filters
        .map((filter: FilterBuilderFilterFlat) => {
          try {
            const param = this.params.find(p => p.id === filter.paramId);

            if (!param) {
              return null;
            }

            return FilterBuilderFilter.unflatten({ ...filter, param });
          } catch {
            return null;
          }
        })
        .filter(filter => !!filter);

      this.filters = data;
    }
    // End Code added by Sanchit on 07/01/2026
    if (this.columnOptions) {
      for (const opt of this.columnOptions) {
        if (savedFilter.columns && savedFilter.columns.length > 0) {
          opt.checked = !!savedFilter.columns.find(
            column => column.name.toLowerCase() === opt.name.toLowerCase()
          );
        } // this should almost certainly be if (savedFilter.exportColumns && ...
        if (savedFilter.columns && savedFilter.exportColumns.length > 0) {
          opt.includeInExport = !!savedFilter.exportColumns.find(
            column => column.name.toLowerCase() === opt.name.toLowerCase()
          );
        }
      }
    }

    // trying to triggerAll when you take off a saved filter
    this.hasAutoTriggered = false;

    this.filtersChanged();

    if (this.columnOptions) {
      if (this.onlyExportOptions) {
        this.updateAndEmitExportColumns();
      } else {
        this.updateAndEmitColumnSet();
      }
    }
  };

  openColumnModal() {
    this.trialColumnOptions = JSON.parse(JSON.stringify(this.columnOptions));
    this.columnOptionsModal.open();
  }

  columnModalClosed() {
    this.columnOptions = JSON.parse(JSON.stringify(this.trialColumnOptions));

    this.updateAndEmitColumnSet();
  }

  columnModalCanceled() {
    this.trialColumnOptions = [];
  }

  dropdownChanged(open: boolean) {
    this.updateAndEmitExportColumns();
  }

  updateAndEmitColumnSet() {
    const columns = this.columnOptions
      .filter(co => co.checked)
      .map(co => ({
        name: co.name,
        header: co.label
      }));

    const exportColumns = this.columnOptions
      .filter(co => co.includeInExport)
      .map(co => ({
        name: co.name,
        header: co.label
      }));

    this.columnsChange.emit({ columns, exportColumns });
  }

  updateAndEmitExportColumns() {
    const exportColumns = this.columnOptions
      .filter(co => co.includeInExport)
      .map(co => co.name);

    this.exportColumnsChange.emit(exportColumns);
  }
  // added by priya 04-02-2026
  isFilterOpen = false;
  currentPlacement: 'top' | 'bottom';
  nestedDropdownMaxHeight = 200;
  openUp = false;

  toggleOptionMenu(event: MouseEvent) {
    this.openOptionMenu = !this.openOptionMenu;

    if (!this.openOptionMenu) {
      return;
    }

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();

    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    this.openUp = spaceAbove > spaceBelow;

    const reservedHeight = 40;

    const availableHeight =
      (this.openUp ? spaceAbove : spaceBelow) - reservedHeight;

    this.nestedDropdownMaxHeight = Math.max(
      200,
      Math.min(680, availableHeight)
    );

  }


  onPopoverShown() {
    this.isFilterOpen = true;

    this.popoverStateChange.emit({
      isOpen: true,
      placement: this.currentPlacement
    });
  }

  onPopoverHidden() {
    this.isFilterOpen = false;

    this.popoverStateChange.emit({
      isOpen: false,
      placement: this.currentPlacement
    });
  }
  @ContentChild('sortByTemplate') sortTemplate!: TemplateRef<any>;
  @Input() isNoData?: boolean = false;
  @Input() hasFewData?: boolean = false;
  @Input() keepSearchVisibleOnTopOpen = false;
  getPlacement(): string {
    return window.innerWidth <= 768 || this.isNoData || this.hasFewData
      ? 'top'
      : 'bottom';
  }
  @Input() totalWorkflows: number = 0;
  @Input() rowIndex: number = 0;
  @Input() dataSetConfig: any;
  @Output() popoverStateChange = new EventEmitter<{
    isOpen: boolean;
    placement: 'top' | 'bottom';
  }>();
  @Input() externalState!: {
    isOpen: boolean;
    placement: 'top' | 'bottom';
  };
  getPlacementOfDatasets(btn?: HTMLElement): string {
    const POPOVER_HEIGHT = 400;
    const lastIndex = this.totalWorkflows - 1;

    if (this.isNoData) return 'top';
    if (!btn) return 'bottom';

    const rect = btn.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    // 1️⃣ Few workflows → all top
    if (this.hasFewData || this.isNoData) {
      return 'top';
    }

    // 2️⃣ Last two rows → always top (avoid cut)
    if (this.rowIndex >= lastIndex - 1) {
      return 'top';
    }
    if (this.rowIndex === 0) {
      return spaceBelow >= POPOVER_HEIGHT ? 'bottom' : 'top';
    }

    // // 3️⃣ First row / middle rows → bottom if enough space, otherwise top
    if (spaceBelow >= POPOVER_HEIGHT) {
      return 'bottom';
    } // enough space below
    if (spaceAbove >= POPOVER_HEIGHT) {
      return 'top';
    } // enough space above

    // 4️⃣ Fallback
    return 'top';
  }

  onOptionSelect(filter, opt) {
    const fakeEvent = {
      srcElement: {
        value: opt.value
      }
    };

    this.bypassBinding(filter, fakeEvent);
    this.openOptionMenu = false;
  }

  onOptionInSelect(filter, opt) {

    let selected = Array.isArray(filter.customValue)
      ? [...filter.customValue]
      : [];

    const index = selected.indexOf(opt.value);

    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(opt.value);
    }

    filter.customValue = selected;
    filter.value = selected.join(',');
    console.log('Filter value: ', filter.value);  // debugging on server purpose only

    this.filtersChanged();
  }
   onRemoveFilter(event: MouseEvent, filter: FilterBuilderFilter): void {
    event.stopPropagation();
    this.removeFilter(filter);
  }
  // end by priya 04-02-2026

  // Added by Vipul Rathod on 17/07/2026
  onColumnCheckboxChanged(event: {
    itemId: string;
    checkboxIndex: number;
    checked: boolean;
  }) {

    const column = this.columnOptions.find(
      c => c.name === event.itemId
    );

    if (!column) {
      return;
    }

    switch (this.columnsDropdownMode) {
      case 'export-only':
        column.includeInExport = event.checked;
        break;
      case 'show-only':
        column.checked = event.checked;
        break;
      case 'show-export':
        if (event.checkboxIndex === 0) {
          column.checked = event.checked;
        }
        if (event.checkboxIndex === 1) {
          column.includeInExport = event.checked;
        }
        break;
    }

    this.allTriggered = this.columnOptions.every(c => c.includeInExport);

    switch (this.columnsDropdownMode) {
      case 'export-only':
        this.updateAndEmitExportColumns();
        break;
      default:
        this.updateAndEmitColumnSet();
        break;
    }

    this.refreshColumnsDropdown();
    this.refreshColumnsDropdown();
  }

  onColumnSelectAll(event) {
    this.triggerAll(event.checked);
    this.allTriggered = event.checked;
    if (this.onlyExportOptions) {
      this.updateAndEmitExportColumns();
    } else {
      this.updateAndEmitColumnSet();
    }
    this.refreshColumnsDropdown();
  }

  private refreshColumnsDropdown(
    mode: ColumnsDropdownMode = this.columnsDropdownMode
  ): void {
    this.columnsDropdownMode = mode;
    let rightHeaders = [];
    let showSelectAll = false;
    switch (mode) {
      case 'export-only':
        rightHeaders = ['Export'];
        showSelectAll = true;
        break;
      case 'show-only':
        rightHeaders = ['Show'];
        break;
      case 'show-export':
        rightHeaders = ['Show', 'Export'];
        break;
    }
    this.columnsDropdownData = {
      ...this.columnsDropdownData,
      placeholder:
        // `COLUMNS (${this.selectedExportColumnsNumber || 'none'} / ${this.columnOptions.length})`,
        `COLUMNS`,
      options: [],
      panelMode: 'checkbox-list',
      checkboxPanel: {
        // title: 'Column Settings',
        leftHeader: 'Column Settings',
        rightHeaders,
        showSelectAll,
        items: this.columnOptions.map(column => ({
          id: column.name,
          label: column.label,
          checks:
            mode === 'export-only'
              ? [
                  {
                    checked: column.includeInExport
                  }
                ]
              : mode === 'show-only'
              ? [
                  {
                    checked: column.checked
                  }
                ]
              : [
                  {
                    checked: column.checked
                  },
                  {
                    checked: column.includeInExport
                  }
                ]
        }))
      }
    };
  }
  // End by Vipul Rathod on 17/07/2026

  // Added by Vipul Rathod on 30-07-2026
  private scrollParent: HTMLElement | null = null;

  private getScrollableParent(el: HTMLElement): HTMLElement | null {
    let parent = el.parentElement;

    while (parent) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;

      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  }

  private lockParentScroll(popoverEl: HTMLElement) {

    this.scrollParent = this.getScrollableParent(popoverEl);

    if (this.scrollParent && this.staffFilterBuilder) {
      this.scrollParent.style.overflowY = 'hidden';
    }
  }

  private unlockParentScroll() {
    if (this.scrollParent && this.staffFilterBuilder) {
      this.scrollParent.style.overflowY = '';
      this.scrollParent = null;
    }
  }
  // End by Vipul Rathod on 30-07-2026
}
